import { useState } from 'react';
import type { InputHTMLAttributes } from 'react';
import './PasswordField.css';

/* A password <input> that carries its own show/hide "eye" toggle.

   It spreads every standard input prop through to the underlying <input>
   (id, value, onChange, placeholder, required, minLength, autoComplete,
   className, …) so it's a drop-in replacement for a plain
   <input type="password" />. `type` is owned internally and cannot be
   overridden. The wrapper is a full-width block, so the input stays a
   descendant of any host form's field selectors and keeps that styling. */
type PasswordFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  /** Optional class for the wrapper div (the input className is passed straight through). */
  wrapperClassName?: string;
};

function EyeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <path d="M6.61 6.61A18.5 18.5 0 0 0 1 12s4 8 11 8a9.12 9.12 0 0 0 5.39-1.61" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

export default function PasswordField({ wrapperClassName, ...inputProps }: PasswordFieldProps) {
  const [visible, setVisible] = useState(false);

  return (
    <div className={wrapperClassName ? `pwfield ${wrapperClassName}` : 'pwfield'}>
      <input {...inputProps} type={visible ? 'text' : 'password'} />
      <button
        type="button"
        className="pwfield__toggle"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        title={visible ? 'Hide password' : 'Show password'}
      >
        {visible ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  );
}
